import React, {useState, useEffect, useRef} from 'react';
import { Row, Col, Card, Collapse } from 'react-bootstrap';
import { Tabulator, PageModule, DownloadModule, ExportModule } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";

Tabulator.registerModule([PageModule, DownloadModule, ExportModule]);

export interface EntityScores {
  entity_id: number;
  entity_name: string;
  govt_lvl: string;
  uniqueness_score: number;
  reasonable_score: number;
  yearly_change_score: number;
  yearly_no_cap_change_score: number;
  reversal_score: number;
  timeliness_score: number;
  detailed_score: number;
  uca_score: number;
  emp_comp_score: number;
  consistency_cafr_score: number;
  consistency_usbe_score: number;
}

interface EntityDataQualityOverviewProps {
  entityDataQualityScoreData: EntityScores[];
  parentEntityName?: string;
  parentEntityId?: number | null;
  formatCurrency: (value: number) => string;
  formatEntityString: (value: string) => string;
  loading?: boolean;
  activeTab?: string;
}

const EntityDataQualityOverview: React.FC<EntityDataQualityOverviewProps> = ({
  entityDataQualityScoreData,
  parentEntityName,
  parentEntityId,
  formatCurrency,
  formatEntityString,
  loading,
  activeTab,
}) => {

  const [showHeaderInfo, setShowHeaderInfo] = useState<boolean>(false);
  const [showDQRHeaderInfo, setShowDQRHeaderInfo] = useState<boolean>(false);
  const tabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const handleDownload = (type: "csv" | "xlsx" | "html" | "json") => {  
    const formattedEntityName = parentEntityName?.toLowerCase()?.replaceAll(" ", "_") || "Entity";
    if (!tabulatorRef.current) return;
    tabulatorRef.current.download(type, `${formattedEntityName}_data_quality.${type}`);
  };

  const formatPercentage = (cell: any): string => {
    const value = cell.getValue();
    if (value == null) return "0.00%";
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    return (100 * numValue).toFixed(2) + "%";
  };

  useEffect(() => {
    if (!tableRef.current) return;

    if (tabulatorRef.current) {
      try {
        tabulatorRef.current.setData(Array.isArray(entityDataQualityScoreData) ? entityDataQualityScoreData : [])
          .then(() => {
            if (tabulatorRef.current) {
              tabulatorRef.current.setLayout(window.innerWidth < 1025 ? "fitDataFill" : "fitColumns");
              tabulatorRef.current.redraw(true);
            }
          });
      } catch (err) {
      }
      return;
    }

    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && window.innerWidth < 1025;

    tabulatorRef.current = new Tabulator(tableRef.current, {
      minHeight: "450px",
      maxHeight: "100%",
      layout: mobile ? "fitDataFill" : "fitColumns",
      pagination: "local",
      paginationSize: 10,
      placeholder: "No Data Available",
      headerSort: true,
      columns: [ 
          {
            titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-landmark'></i> Entity";
            return el;
            },
            title: "Entity",
            field: "entity_name",
            headerSort: true,
          },
          {
            titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-landmark-dome'></i> Gov. Lvl";
            return el;
            },
            title: "Gov. Lvl",
            field: "govt_lvl",
            headerSort: true,
          },
          {
            titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-copy'></i> Duplicate Reports";
            return el;
            },
            title: "Duplicate Reports",
            field: "uniqueness_score",
            formatter: formatPercentage,
            headerSort: true,
          },
          {
            titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-scale-balanced'></i> AFR Comparison";
            return el;
            },
            title: "AFR Comparison",
            field: "consistency_cafr_score",
            formatter: formatPercentage,
            headerSort: true,
          },
          {
            titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-industry'></i> Vendor Name";
            return el;
            },
            title: "Vendor Name",
            field: "detailed_score",
            formatter: formatPercentage,
            headerSort: true,
          },
          {
            titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-file-circle-check'></i> UCA Validity";
            return el;
            },
            title: "UCA Score",
            field: "uca_score",
            formatter: formatPercentage,
            headerSort: true,
          },
          {
            titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-chart-line'></i> Yearly Total Change";
            return el;
            },
            title: "Yearly Change Score",
            field: "yearly_change_score",
            formatter: formatPercentage,
            headerSort: true,
          },
          {
            titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-money-check-dollar'></i> Payroll Consistency";
            return el;
            },
            title: "Emp Comp Score",
            field: "emp_comp_score",
            formatter: formatPercentage,
            headerSort: true,
          },
          {
            titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-objects-landmark'></i> Reversals";
            return el;
            },
            title: "Reversal Score",
            field: "reversal_score",
            formatter: formatPercentage,
            headerSort: true,
          },
      ],
      data: Array.isArray(entityDataQualityScoreData) ? entityDataQualityScoreData : [],
    });

    setTimeout(() => {
      if (tabulatorRef.current) tabulatorRef.current.redraw(true);
    }, 100);

    const handleResize = () => {
      if (!tabulatorRef.current) return;
      const mobileResize = window.innerWidth < 1025;
      tabulatorRef.current.setLayout(mobileResize ? "fitDataFill" : "fitColumns");
      tabulatorRef.current.redraw(true);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (tabulatorRef.current) {
          tabulatorRef.current.destroy();
          tabulatorRef.current = null;
      }
    };
  }, [loading, activeTab, entityDataQualityScoreData]);

  return (
    <Row className="mb-2">
      <Col xs={12}>
        <Card bg="secondary" text="white" style={{ border: "none", borderRadius: "0" }}>
          <Card.Body style={{ justifyItems: "center" }}>
            <div className="tu_tabHeader">
              <div className="tu-tabHeader__titleRow">
                <h1 className="tu-tabHeader__title">
                Data Quality</h1>
                <button
                  type="button"
                  className="tu-tabHeader__infoBtn"
                  onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                  aria-label="Data Quality info"
                >
                  <i className="fas fa-info"/>
                </button>
              </div>
            </div>
          </Card.Body>
        </Card>

        {showHeaderInfo ? (
        <Card>
          <Card.Body>
            <Collapse in={showHeaderInfo}>
              <div className="data-quality-info">
                <p className="mt-2">
                  The Data Quality tab flags errors and quality concerns in Transparency reports submitted to the Office of the State Auditor.
                </p>
                <p className="mt-2">
                  Our team has developed algorithms and metrics to pinpoint issues in the data that cause confusion for users of Transparent Utah. This tab is meant to be another useful tool for entities who submit to Transparent Utah to understand how the data they have submitted looks to the public. This tool can also help speed up the resolution of data quality issues since the visualizations and explanations can help uploaders understand how to rectify their scores.
                </p>
                <p className="lead text-uppercase fw-bolder">
                  Why is Data Quality Important?
                </p>
                <p>
                  Bad data can confuse and impair decision making. Transparent Utah data is accessed by tens of thousands of Utahns every month. The data submitted to Transparent Utah is also used by the Utah State Legislature, the Federal Census Bureau, the Office of the State Auditor, and a variety of other research and government organizations.
                </p>
                <p className="lead text-uppercase fw-bolder">
                  What are the Data Quality Scores?
                </p>
                <Row id="overview-score-explanation-holder">
                  <Col xs={12} sm={6} md={6} lg={6} xl={6} className="px-2 py-2">
                    <Card className="overview-score explanation h-100">
                      <Card.Body className="bg-secondary text-light rounded">
                        <h2 className="text-white">1. Missing Reports</h2>
                        <p className="card-text align-bottom pt-2">Checks if an entity is missing any Transparency Reports</p>
                        </Card.Body>
                    </Card>
                  </Col>

                  <Col xs={12} sm={6} md={6} lg={6} xl={6} className="px-2 py-2">
                    <Card className="overview-score explanation h-100">
                      <Card.Body className="bg-secondary text-light rounded">
                        <h2 className="text-white">2. Duplicate Reports</h2>
                        <p className="card-text align-bottom pt-2">Uses algorithms to flag reports that may have duplicate data</p>
                        </Card.Body>
                    </Card>
                  </Col>

                  <Col xs={12} sm={6} md={6} lg={6} xl={6} className="px-2 py-2">
                    <Card className="overview-score explanation h-100">
                      <Card.Body className="bg-secondary text-light rounded">
                        <h2 className="text-white">3. AFR Comparison</h2>
                        <p className="card-text align-bottom pt-2"> How well the data in Transparent Utah matches data reported in the Annual Financial Report (AFR)</p>
                        </Card.Body>
                    </Card>
                  </Col>

                  <Col xs={12} sm={6} md={6} lg={6} xl={6} className="px-2 py-2">
                    <Card className="overview-score explanation h-100">
                      <Card.Body className="bg-secondary text-light rounded">
                        <h2 className="text-white">4. Vendor Name %</h2>
                        <p className="card-text align-bottom pt-2">The rate of vendor, payee, and employee names that have a non redacted name assigned to them</p>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xs={12} sm={6} md={6} lg={6} xl={6} className="px-2 py-2">
                    <Card className="overview-score explanation h-100">
                      <Card.Body className="bg-secondary text-light rounded">
                        <h2 className="text-white">5. UCA Validity</h2>
                        <p className="card-text align-bottom pt-2">  The rate of vendor, payee, and employee names that have a non redacted name assigned to them</p>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xs={12} sm={6} md={6} lg={6} xl={6} className="px-2 py-2">
                    <Card className="overview-score explanation h-100">
                      <Card.Body className="bg-secondary text-light rounded">
                        <h2 className="text-white">6. Yearly Total Change</h2>
                        <p className="card-text align-bottom pt-2">  Checks for large changes in the year to year totals for expenditure, revenue, and payroll</p>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xs={12} sm={6} md={6} lg={6} xl={6} className="px-2 py-2">
                    <Card className="overview-score explanation h-100">
                      <Card.Body className="bg-secondary text-light rounded">
                        <h2 className="text-white">7. Payroll Consistency</h2>
                        <p className="card-text align-bottom pt-2">  Checks for large changes in individual employee's compensation</p>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xs={12} sm={6} md={6} lg={6} xl={6} className="px-2 py-2">
                    <Card className="overview-score explanation h-100">
                      <Card.Body className="bg-secondary text-light rounded">
                        <h2 className="text-white">8. Reversals</h2>
                        <p className="card-text align-bottom pt-2">  Examines the percentage of transactions that have a reversal.</p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>
            </Collapse>
          </Card.Body>
        </Card>
        ) : (
        <></>
        )}

        {entityDataQualityScoreData.length > 0 ? (
          <>
            <Card className="mb-4">
              <Row className="py-4 px-4">
                <Col>
                  <div className="mb-2 d-flex gap-2">
                    <button className="btn btn-secondary" onClick={() => handleDownload("csv")}>CSV</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("xlsx")}>Excel</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("html")}>HTML</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("json")}>JSON</button>
                  </div>
                  <div style={{ width: "100%" }}>
                    <div ref={tableRef} className="table-light" style={{ minHeight: 400 }} />
                  </div>
                </Col>
              </Row>
            </Card>

            {/* Section Header Card */}
            <Card bg="secondary" text="white" style={{ border: "none", borderRadius: "0" }}>
              <Card.Body style={{ justifyItems: "center" }}>
                <div className="tu_tabHeader">
                  <div className="tu-tabHeader__titleRow">
                    <h1 className="tu-tabHeader__title">Data Quality Report Cards</h1>
                    <button
                      type="button"
                      className="tu-tabHeader__infoBtn"
                      onClick={() => setShowDQRHeaderInfo(!showDQRHeaderInfo )}
                      aria-label="Data Quality Report Card info"
                    >
                      <i className="fas fa-info" />
                    </button>
                  </div>
                </div>

                <Collapse in={showDQRHeaderInfo}>
                  <div className="data-quality-report-cards">
                    This section shows data quality report cards for the selected entity. Any score above 95% is a passing rate and a failing score is below 85%.
                  </div>
                </Collapse>
              </Card.Body>
            </Card>

            {/* Missing Reports */}
            {entityDataQualityScoreData[0]?.timeliness_score > 0.95 ? (
            <>
              <Card className="mb-4 text-dark" style={{borderRadius: "0"}}>
                <h4 className="card-header">
                  Missing Reports
                </h4>
                <Row className="py-0 px-0">
                  <Col className="py-2 px-2">
                    <Card>
                      <Card.Body className="bg-success text-light text-center">
                        <i className="fas fa-4x fa-check-circle big-centered-icon" aria-hidden="true"></i>
                        <Card.Title className="mt-2">{"Passed (> 95%)"}</Card.Title>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Card>
            </>
            ) : (
              <>
                {/* Top Header Card */}
                <Card className="mb-4 text-dark">
                  <h4 className="card-header">
                    Missing Reports
                  </h4>
                  <Row className="py-0 px-0">
                    <Col className="py-2 px-2">
                      <Card>
                        <Card.Body className="bg-danger text-light text-center">
                          <i className="fas fa-4x fa-times-circle big-centered-icon" aria-hidden="true"></i>
                          <Card.Title className="mt-2">{"Failed (< 85%)"}</Card.Title>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Card>
              </>
            )}
            {/* End Missing Reports */}

            {/* Duplicate Reports */}
            {entityDataQualityScoreData[0]?.uniqueness_score > 0.95 ? (
              <>
                <Card className="mb-4 mt-3 text-dark">
                  <h4 className="card-header">
                    Duplicate Reports
                  </h4>
                  <Row className="py-0 px-0">
                    <Col className="py-2 px-2">
                      <Card>
                        <Card.Body className="bg-success text-light text-center">
                          <i className="fas fa-4x fa-check-circle big-centered-icon" aria-hidden="true"></i>
                          <Card.Title className="mt-2">{"Passed (> 95%)"}</Card.Title>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Card>
              </>
              ) : (
                <>
                  <Card className="mb-4 mt-3 text-dark">
                    <h4 className="card-header">
                      Duplicate Reports
                    </h4>
                    <Row className="py-0 px-0">
                      <Col className="py-2 px-2">
                        <Card>
                          <Card.Body className="bg-danger text-light text-center">
                            <i className="fas fa-4x fa-times-circle big-centered-icon" aria-hidden="true"></i>
                            <Card.Title className="mt-2">{"Failed (< 85%)"}</Card.Title>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Card>
                </>
              )}
              {/* End Duplicate Reports */}

              {/* AFR Comparison */}
              {entityDataQualityScoreData[0]?.consistency_cafr_score > 0.95 ? (
                <>
                  <Card className="mb-4 text-dark">
                    <h4 className="card-header">
                      AFR Comparison
                    </h4>
                    <Row className="py-0 px-0">
                      <Col className="py-2 px-2">
                        <Card>
                          <Card.Body className="bg-success text-light text-center">
                            <i className="fas fa-4x fa-check-circle big-centered-icon" aria-hidden="true"></i>
                            <Card.Title className="mt-2">{"Passed (> 95%)"}</Card.Title>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Card>
                </>
                ) : (
                  <>
                    <Card className="mb-4 text-dark">
                      <h4 className="card-header">
                        AFR Comparison
                      </h4>
                      <Row className="py-0 px-0">
                        <Col className="py-2 px-2">
                          <Card>
                            <Card.Body className="bg-danger text-light text-center">
                              <i className="fas fa-4x fa-times-circle big-centered-icon" aria-hidden="true"></i>
                              <Card.Title className="mt-2">{"Failed (< 85%)"}</Card.Title>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Card>
                  </>
                )}
              {/* End AFR Comparison */}

              {/* Vendor Name Percentage */}
              {entityDataQualityScoreData[0]?.detailed_score > 0.95 ? (
                <>
                  <Card className="mb-4 text-dark">
                    <h4 className="card-header">
                      Vendor Name %
                    </h4>
                    <Row className="py-0 px-0">
                      <Col className="py-2 px-2">
                        <Card>
                          <Card.Body className="bg-success text-light text-center">
                            <i className="fas fa-4x fa-check-circle big-centered-icon" aria-hidden="true"></i>
                            <Card.Title className="mt-2">{"Passed (> 95%)"}</Card.Title>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Card>
                </>
                ) : (
                  <>
                    <Card className="mb-4 text-dark">
                      <h4 className="card-header">
                        Vendor Name %
                      </h4>
                      <Row className="py-0 px-0">
                        <Col className="py-2 px-2">
                          <Card>
                            <Card.Body className="bg-danger text-light text-center">
                              <i className="fas fa-4x fa-times-circle big-centered-icon" aria-hidden="true"></i>
                              <Card.Title className="mt-2">{"Failed (< 85%)"}</Card.Title>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Card>
                  </>
                )}
              {/* End Vendor Name Percentage */}

              {/* Vendor Name Percentage */}
              {entityDataQualityScoreData[0]?.uca_score > 0.95 ? (
                <>
                  <Card className="mb-4 text-dark">
                    <h4 className="card-header">
                      UCA Validity
                    </h4>
                    <Row className="py-0 px-0">
                      <Col className="py-2 px-2">
                        <Card>
                          <Card.Body className="bg-success text-light text-center">
                            <i className="fas fa-4x fa-check-circle big-centered-icon" aria-hidden="true"></i>
                            <Card.Title className="mt-2">{"Passed (> 95%)"}</Card.Title>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Card>
                </>
                ) : (
                  <>
                    <Card className="mb-4 text-dark">
                      <h4 className="card-header">
                        UCA Validity
                      </h4>
                      <Row className="py-0 px-0">
                        <Col className="py-2 px-2">
                          <Card>
                            <Card.Body className="bg-danger text-light text-center">
                              <i className="fas fa-4x fa-times-circle big-centered-icon" aria-hidden="true"></i>
                              <Card.Title className="mt-2">{"Failed (< 85%)"}</Card.Title>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Card>
                  </>
                )}
              {/* End UCA Validity */}
            
              {/* Yearly Total Change */}
              {entityDataQualityScoreData[0]?.yearly_change_score > 0.95 ? (
                <>
                  <Card className="mb-4 text-dark">
                    <h4 className="card-header">
                      Yearly Total Change
                    </h4>
                    <Row className="py-0 px-0">
                      <Col className="py-2 px-2">
                        <Card>
                          <Card.Body className="bg-success text-light text-center">
                            <i className="fas fa-4x fa-check-circle big-centered-icon" aria-hidden="true"></i>
                            <Card.Title className="mt-2">{"Passed (> 95%)"}</Card.Title>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Card>
                </>
                ) : (
                  <>
                    <Card className="mb-4 text-dark">
                      <h4 className="card-header">
                        Yearly Total Change
                      </h4>
                      <Row className="py-0 px-0">
                        <Col className="py-2 px-2">
                          <Card>
                            <Card.Body className="bg-danger text-light text-center">
                              <i className="fas fa-4x fa-times-circle big-centered-icon" aria-hidden="true"></i>
                              <Card.Title className="mt-2">{"Failed (< 85%)"}</Card.Title>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Card>
                  </>
                )}
              {/* End Yearly Total Change */}

              {/* Payroll Consistency */}
              {entityDataQualityScoreData[0]?.emp_comp_score > 0.95 ? (
                <>
                  <Card className="mb-4 text-dark">
                    <h4 className="card-header">
                      Payroll Consistency
                    </h4>
                    <Row className="py-0 px-0">
                      <Col className="py-2 px-2">
                        <Card>
                          <Card.Body className="bg-success text-light text-center">
                            <i className="fas fa-4x fa-check-circle big-centered-icon" aria-hidden="true"></i>
                            <Card.Title className="mt-2">{"Passed (> 95%)"}</Card.Title>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Card>
                </>
                ) : (
                  <>
                    <Card className="mb-4 text-dark">
                      <h4 className="card-header">
                        Payroll Consistency
                      </h4>
                      <Row className="py-0 px-0">
                        <Col className="py-2 px-2">
                          <Card>
                            <Card.Body className="bg-danger text-light text-center">
                              <i className="fas fa-4x fa-times-circle big-centered-icon" aria-hidden="true"></i>
                              <Card.Title className="mt-2">{"Failed (< 85%)"}</Card.Title>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Card>
                  </>
                )}
              {/* Payroll Consistency */}

              {/* Reversals*/}
              {entityDataQualityScoreData[0]?.reversal_score > 0.95 ? (
                <>
                  <Card className="mb-4 text-dark">
                    <h4 className="card-header">
                      Reversals
                    </h4>
                    <Row className="py-0 px-0">
                      <Col className="py-2 px-2">
                        <Card>
                          <Card.Body className="bg-success text-light text-center">
                            <i className="fas fa-4x fa-check-circle big-centered-icon" aria-hidden="true"></i>
                            <Card.Title className="mt-2">{"Passed (> 95%)"}</Card.Title>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Card>
                </>
                ) : (
                  <>
                    <Card className="mb-4 text-dark">
                      <h4 className="card-header">
                        Reversals
                      </h4>
                      <Row className="py-0 px-0">
                        <Col className="py-2 px-2">
                          <Card>
                            <Card.Body className="bg-danger text-light text-center">
                              <i className="fas fa-4x fa-times-circle big-centered-icon" aria-hidden="true"></i>
                              <Card.Title className="mt-2">{"Failed (< 85%)"}</Card.Title>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Card>
                  </>
                )}
              {/* Reversals */}

          </>
        ) : (
            <>
              <Card className="mb-4">
                <div className="p-5 text-center">
                  <div className="alert alert-light shadow-sm">
                    No data available
                  </div>
                </div>
              </Card>
            </>
          ) }
      </Col>
    </Row>
  )
}

export default EntityDataQualityOverview;